/**
 * **再生中の fps を舞台の隅に出す**（`fpsStats.js`）。書き出しには出さない（呼ぶ側が出さない）。
 *
 * 数えるのは**舞台を描き直した回数**（`ms` が変わった描画）と、ブラウザの画面の更新（requestAnimationFrame）。
 * 画面の更新まで落ちていればページ全体が詰まっている（重い描画）、画面は 60 のまま描画だけ落ちていれば
 * 素材の時計（動画のフレーム）が来ていない。
 */
import { useEffect, useRef, useState } from 'react';
import { fpsStats, pushSample } from './fpsStats';

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export default function FpsMeter({ ms, playing, fps = 30, style = null }) {
  const samples = useRef([]);
  // 描き直すたびに積む（子の舞台の effect のあと＝描き終えた時刻）
  useEffect(() => { samples.current = pushSample(samples.current, { t: now(), ms }); }, [ms]);

  // 表示は 4 回 / 秒だけ更新する（毎フレーム state を変えると、それ自体が重くなる）
  const [stats, setStats] = useState(null);
  const [screenFps, setScreenFps] = useState(0);
  useEffect(() => {
    if (!playing || typeof window === 'undefined') { setStats(null); return undefined; }
    let frames = 0;
    let from = now();
    let raf = 0;
    const tick = () => { frames += 1; raf = window.requestAnimationFrame(tick); };
    raf = window.requestAnimationFrame(tick);
    const timer = window.setInterval(() => {
      const t = now();
      setStats(fpsStats(samples.current, t, fps));
      setScreenFps(Math.round((frames * 1000) / Math.max(1, t - from)));
      frames = 0; from = t;
    }, 250);
    return () => { window.cancelAnimationFrame(raf); window.clearInterval(timer); };
  }, [playing, fps]);

  const color = !stats ? '#aaa' : (stats.smooth ? '#7ee787' : (stats.fps >= fps * 0.7 ? '#f0c040' : '#ff7b72'));
  return (
    <div
      data-testid="fps-meter"
      style={{
        position: 'absolute', left: 6, top: 6, zIndex: 20, pointerEvents: 'none',
        background: 'rgba(0,0,0,0.65)', color, font: '11px/1.35 ui-monospace, Menlo, monospace',
        padding: '3px 6px', borderRadius: 4, whiteSpace: 'pre', ...(style ?? {}),
      }}
    >
      {stats
        ? `描画 ${stats.fps.toFixed(1)} / ${fps} fps  画面 ${screenFps}\nコマ飛び ${stats.skipped}  最長 ${stats.worstMs}ms\n${stats.smooth ? '描画は追いついている（カクつきは演出の動き）' : '描画が追いついていない'}`
        : '停止中（再生すると fps を出す）'}
    </div>
  );
}
